import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, easings, useTransition } from '@react-spring/web';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, LabelL } from '@sorare/core/src/atoms/typography';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useFormatAnyPosition } from '@sorare/core/src/lib/players';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { CardProperties } from '@sorare/marketplace/src/components/card/CardProperties';

import type { CardReveal_anyCard } from './__generated__/index.graphql.ts';
import { useBackgroundY } from './useBackgroundY.ts';

const rotation = keyframes`
  from {
    transform: rotateY(83deg);
  }
  to {
    transform: rotateY(0deg);
  }
`;
const letterSpacing = keyframes`
  from {
    letter-spacing: 100px;
  }
  to {
    letter-spacing: 0px;
  }
`;
const blur = keyframes`
  from {
    filter: blur(50px);
  }
  to {
    filter: blur(0px);
  }
`;
const Root = styled(animated.div)`
  --y-offset: 0px;

  position: fixed;
  inset: 0;
  overflow: hidden;
  background: radial-gradient(transparent, var(--c-black) 70%),
    repeating-linear-gradient(
      to right,
      transparent,
      var(--c-nd-500) 1px,
      transparent 2px,
      transparent 60px
    ),
    repeating-linear-gradient(
      to bottom,
      transparent var(--y-offset),
      var(--c-nd-500) calc(var(--y-offset) + 1px),
      transparent calc(var(--y-offset) + 2px),
      transparent calc(var(--y-offset) + 60px)
    ),
    var(--c-black);

  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in-out forwards;
`;
const RelativeWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: 100%;
  height: 100%;
`;
const RevealElement = styled(animated.div)`
  position: absolute;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PositionText = styled.span`
  font-family: var(--brand-font);
  font-size: max(calc(7 * var(--vw)), 48px);
  font-weight: bold;
  text-transform: uppercase;
  text-shadow:
    0 0 4px rgba(255, 255, 255, 1),
    0 0 6px rgba(255, 255, 255, 0.6),
    0 0 8px rgba(255, 255, 255, 0.2);
  max-width: 100%;

  letter-spacing: 100px;
  filter: blur(50px);

  animation:
    1000ms ${letterSpacing} 750ms cubic-bezier(0, 0.55, 0.45, 1) forwards,
    1000ms ${blur} 750ms cubic-bezier(0, 0.55, 0.45, 1) forwards;
`;
const PictureShadow = styled(Horizontal).attrs({ center: true })`
  --shadow-color: var(--c-white);

  width: 100%;
  filter: drop-shadow(0 0 var(--unit) var(--shadow-color));
`;
const Picture = styled.img`
  width: 15%;
  min-width: 100px;
  max-width: 90%;
`;
const CardWrapper = styled(Vertical).attrs({ center: true, gap: 2 })`
  width: 80%;
`;
const FadeIn = styled(Vertical).attrs({ center: true })`
  opacity: 0;
  animation: 500ms ${fadeIn} 2s ease-in-out forwards;
`;
const CardPicture = styled(Picture)`
  min-width: 200px;
  transform: rotateY(83deg);

  animation: 750ms ${rotation} 1000ms cubic-bezier(0, 0.55, 0.45, 1) forwards;

  @media ${tabletAndAbove} {
    min-width: 300px;
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

type Props = {
  card: CardReveal_anyCard;
  onAnimationEnd?: () => void;
};
export const CardReveal = ({ card, onAnimationEnd }: Props) => {
  const ended = useRef(false);
  const [i, setI] = useState(0);
  const formatPosition = useFormatAnyPosition();

  const steps = useMemo(
    () =>
      [
        <Vertical key={card.slug} center>
          {card.anyPositions.map(anyPosition => (
            <PositionText key={anyPosition}>
              {formatPosition(anyPosition)}
            </PositionText>
          ))}
        </Vertical>,
        card.anyPlayer.country.code && (
          <PictureShadow>
            <Picture
              key={card.anyPlayer.country.slug}
              src={`${FRONTEND_ASSET_HOST}/flags/${card.anyPlayer.country.code}.svg`}
              alt=""
            />
          </PictureShadow>
        ),
        card.anyPlayer.activeClub?.pictureUrl && (
          <PictureShadow>
            <Picture
              key={card.anyPlayer.activeClub.slug}
              src={card.anyPlayer.activeClub.pictureUrl}
              alt=""
            />
          </PictureShadow>
        ),
        card.pictureUrl && (
          <CardWrapper>
            <FadeIn gap={0}>
              <BrandL>
                <FormattedMessage defaultMessage="New signing" />
              </BrandL>
              <LabelL>
                <FormattedMessage
                  defaultMessage="Welcome {name} to your team"
                  values={{ name: card.anyPlayer.displayName }}
                />
              </LabelL>
            </FadeIn>
            <PictureShadow
              style={
                {
                  '--shadow-color': getRarityColor(card.rarityTyped, {
                    opacity: 0.75,
                  }),
                } as CSSProperties
              }
            >
              <CardPicture key={card.slug} src={card.pictureUrl} alt="" />
            </PictureShadow>
            <FadeIn gap={1.5}>
              <CardProperties
                card={card}
                cardWithStats={card}
                withTransferMalus={false}
              />
              <Button
                size="medium"
                color="secondary"
                onClick={onAnimationEnd}
                fullWidth
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
            </FadeIn>
          </CardWrapper>
        ),
      ].filter(Boolean),
    [card, formatPosition, onAnimationEnd]
  );

  const isLastStep = i >= steps.length - 1;
  const transitions = useTransition(i, {
    from: {
      opacity: -1,
      scale: 1.5,
      y: 1000,
    },
    enter: {
      opacity: 1,
      scale: 1,
      y: 0,
      delay: 750,
      config: {
        duration: 1000,
        easing: easings.easeOutCubic,
      },
      onRest: () => {
        if (!isLastStep) {
          setI(prev => prev + 1);
        }
      },
    },
    leave: {
      opacity: -2,
      scale: 0.3,
      y: -1000,
      delay: 500,
      config: {
        duration: 1000,
        easing: easings.easeInCirc,
      },
    },
  });

  if (i === steps.length && !ended.current) {
    onAnimationEnd?.();
    ended.current = true;
  }

  return (
    <Root
      style={
        {
          '--y-offset': useBackgroundY({
            count: steps.slice(0, -1).length,
          }).to(y => `${y}px`),
        } as CSSProperties
      }
    >
      <RelativeWrapper>
        {!!onAnimationEnd && (
          <CloseButtonWrapper>
            <CloseButton size="medium" onClose={onAnimationEnd} />
          </CloseButtonWrapper>
        )}
        {transitions((style, node) => {
          const step = steps[node];
          return <RevealElement style={style}>{step}</RevealElement>;
        })}
      </RelativeWrapper>
    </Root>
  );
};
CardReveal.fragments = {
  anyCard: gql`
    fragment CardReveal_anyCard on AnyCardInterface {
      slug
      pictureUrl
      anyPositions
      rarityTyped
      anyPlayer {
        slug
        displayName
        activeClub {
          slug
          pictureUrl
        }
        country {
          slug
          code
        }
      }
      ...CardPropertiesWithStats_anyCard
      ...CardProperties_anyCard
    }
    ${CardProperties.fragments.anyCard}
    ${CardProperties.fragments.anyCardWithStats}
  ` as TypedDocumentNode<CardReveal_anyCard>,
};
